import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Form,
  Input,
  Typography,
  Row,
  notification,
  Space,
  Tag
} from 'antd'
import * as Icons from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import produce from 'immer'

import { Queries } from 'admin/lib/Api'
import { BodyContentColumn } from 'admin/lib/Layouts'
import { FormItem, SectionProvider, Section } from 'admin/lib/Forms'
import { isoDateFromFormDate, isoDateToFormDate } from 'admin/lib/dates'
import * as ROUTES from 'admin/routes'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import {
  CheckCertificationNumber,
  CheckCertificationResourceURI,
  CheckCertificationScheme,
  CheckCompanyAddress,
  CheckCompanyName,
  CheckCompanyNumber,
  CheckCompanyResourceURI,
  CheckExpiryDateAfterValidFromDate
} from 'admin/lib/SimpleDataValidation'
import { Matchers } from 'admin/lib/Records'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { Guid } from 'admin/lib/guid'

const DEFAULT_CERTIFICATE = {
  certificateId: Guid.Empty,
  certificateNumber: '',
  certificationScheme: '',
  validFromDate: null,
  expirationDate: null,
  resourceURI: '',
  createdAtDate: null
}

export function CompanyEditForm({ company, isNew = false }) {
  const navigate = useNavigate()
  const companyUpdate = Queries.useCompanyUpsert(isNew)

  //
  // Form Setup

  const form = useForm({
    mode: 'all'
  })
  const { reset } = form

  const certificatesFields = useFieldArray({
    control: form.control,
    name: 'companyCertificates'
  })

  useEffect(() => {
    if (company) {
      reset(transformCompanyToFormData(company))
    }
  }, [company, reset])

  //
  // Callbacks

  const navigateToList = useCallback(() => {
    navigate(ROUTES.COMPANIES.LIST)
  }, [navigate])

  const onSubmit = useCallback(
    async values => {
      const currentCompany = company
      const nextCompany = transformFormDataToCompany(currentCompany, values)

      await companyUpdate.mutate(
        { company: nextCompany },
        {
          onSuccess: () => {
            notification.success({
              message: isNew
                ? 'Successfully created company'
                : 'Successfully updated company'
            })
            navigateToList()
          }
        }
      )
    },
    [company, companyUpdate, isNew, navigateToList]
  )
  const handleSubmit = form.handleSubmit(onSubmit)

  useGlobalShortcut(Matchers.form.save, () => handleSubmit())

  if (companyUpdate.isLoading) {
    return <ScreenSpin />
  }

  return (
    <FormProvider {...form}>
      <SectionProvider>
        <BodyContentColumn>
          <Form onFinish={handleSubmit} layout="vertical">
            {companyUpdate.isError && (
              <Typography.Text type="danger">
                {JSON.stringify(companyUpdate.error.response.data)}
              </Typography.Text>
            )}

            <Section>
              {({ firstInputRef }) => (
                <>
                  <Typography.Title level={3}>
                    {isNew ? 'New Company' : 'Edit Company'}
                  </Typography.Title>

                  <FormItem
                    name="name"
                    label="Company Name"
                    inputRef={firstInputRef}
                    id="companyName"
                    simpleDataValidationRules={CheckCompanyName}
                  />

                  <FormItem
                    name="companyNumber"
                    label="Company Number"
                    id="companyNumber"
                    simpleDataValidationRules={CheckCompanyNumber}
                  />

                  <FormItem
                    name="address"
                    label="Company Address"
                    id="companyAddress"
                    simpleDataValidationRules={CheckCompanyAddress}
                  >
                    <Input.TextArea />
                  </FormItem>

                  <FormItem
                    name="resourceURI"
                    label="Resource URI"
                    id="url"
                    simpleDataValidationRules={CheckCompanyResourceURI}
                  />

                  {company.mergedCompanies?.length > 0 && (
                    <>
                      <Typography.Text level={5}>
                        Merged Companies
                      </Typography.Text>
                      <br />
                      <div
                        style={{
                          overflowY: 'scroll',
                          maxHeight: '50px'
                        }}
                      >
                        {company.mergedCompanies?.map((merged, i) => {
                          return <Tag>{merged.name}</Tag>
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </Section>

            {certificatesFields.fields.map((certificate, i) => {
              const field = name => `companyCertificates[${i}].${name}`
              const getProps = name => ({
                name: field(name),
                defaultValue: certificate[name]
              })

              return (
                <Section key={certificate.id}>
                  {({ firstInputRef }) => (
                    <>
                      <Row justify="space-between">
                        <Typography.Title level={5}>
                          Certificate {i}
                        </Typography.Title>

                        <Button
                          type="ghost"
                          danger
                          onClick={() => certificatesFields.remove(i)}
                          icon={<Icons.DeleteFilled />}
                          tabIndex="-1"
                        />
                      </Row>

                      <FormItem
                        {...getProps('certificateNumber')}
                        label="Certificate Number"
                        inputRef={firstInputRef}
                        simpleDataValidationRules={CheckCertificationNumber}
                      />

                      <FormItem
                        {...getProps('certificationScheme')}
                        label="Certification Scheme"
                        simpleDataValidationRules={CheckCertificationScheme}
                      />

                      <FormItem
                        {...getProps('validFromDate')}
                        label="Valid From"
                      >
                        <Input type="datetime-local" max="9999-12-31T23:59" />
                      </FormItem>

                      <FormItem
                        {...getProps('expirationDate')}
                        label="Expires"
                        simpleDataValidationRules={v =>
                          CheckExpiryDateAfterValidFromDate(
                            v,
                            form.getValues(getProps('validFromDate').name)
                          )
                        }
                      >
                        <Input type="datetime-local" max="9999-12-31T23:59" />
                      </FormItem>

                      <FormItem
                        {...getProps('resourceURI')}
                        label="Resource URI"
                        simpleDataValidationRules={
                          CheckCertificationResourceURI
                        }
                      />

                      <FormItem hidden {...getProps('certificateId')} />
                    </>
                  )}
                </Section>
              )
            })}

            <Section>
              {({ firstInputRef }) => (
                <Button
                  type="dashed"
                  style={{ width: '100%' }}
                  onClick={() =>
                    certificatesFields.append(DEFAULT_CERTIFICATE, true)
                  }
                  icon={<Icons.FileAddOutlined />}
                  ref={firstInputRef}
                >
                  Add Certificate
                </Button>
              )}
            </Section>

            <Row justify="center">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ width: '8rem' }}
                >
                  Save
                </Button>

                <Button onClick={navigateToList} style={{ width: '8rem' }}>
                  Cancel
                </Button>
              </Space>
            </Row>
          </Form>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}

CompanyEditForm.propTypes = {
  company: PropTypes.object.isRequired,
  isNew: PropTypes.bool
}

export function transformCompanyToFormData(company) {
  return produce(company, draft => {
    for (const cert of draft.companyCertificates) {
      cert.validFromDate = isoDateToFormDate(cert.validFromDate)
      cert.expirationDate = isoDateToFormDate(cert.expirationDate)
    }
  })
}

export function transformFormDataToCompany(currentCompany, values) {
  console.log(values)
  return produce(currentCompany, draft => {
    Object.assign(draft, values)

    draft.companyCertificates = (values.companyCertificates ?? []).map(
      formCert => {
        const currentCert = (currentCompany.companyCertificates ?? []).find(
          currentCert => formCert.certificateId === currentCert.certificateId
        )

        return produce(currentCert ?? {}, draftCert => {
          Object.assign(draftCert, formCert)

          draftCert.validFromDate = isoDateFromFormDate(formCert.validFromDate)
          draftCert.expirationDate = isoDateFromFormDate(
            formCert.expirationDate
          )
          if (!draftCert.createdAtDate) {
            delete draftCert.createdAtDate
          }
        })
      }
    )
  })
}
